import React, { useState, useEffect } from 'react';
import { LayoutGrid, Save, RefreshCw } from 'lucide-react';
import { getAvailableFolders, getServiceFiles, isRouteUnique } from '../../utils/autoServiceScanner';

interface TenantTemplatesProps {
  formData: any;
  selectedPage: string;
  setSelectedPage: (page: string) => void;
  pendingTemplateChanges: Record<string, string>;
  setPendingTemplateChanges: (changes: Record<string, string>) => void;
  templatePaths: string[];
  headerOptions: Array<{ value: string; label: string }>;
  footerOptions: Array<{ value: string; label: string }>;
  handlePendingTemplateChange: (page: string, templatePath: string) => void;
  applyPendingTemplateChanges: () => void;
  setHeaderTemplateSelection: (page: 'default' | 'home' | 'contact' | 'about', pathValue: string) => void;
  setFooterTemplateSelection: (page: 'default' | 'home' | 'contact' | 'about', pathValue: string) => void;
  adminHeaderOptions: Array<{ value: string; label: string }>;
  setAdminHeaderTemplateSelection: (page: 'default', pathValue: string) => void;
  updateServicesConfig: (config: any) => void;
}

export const TenantTemplates: React.FC<TenantTemplatesProps> = ({
  formData,
  selectedPage,
  setSelectedPage,
  pendingTemplateChanges,
  setPendingTemplateChanges,
  templatePaths,
  headerOptions,
  footerOptions,
  handlePendingTemplateChange,
  applyPendingTemplateChanges,
  setHeaderTemplateSelection,
  setFooterTemplateSelection,
  adminHeaderOptions,
  setAdminHeaderTemplateSelection,
  updateServicesConfig,
}) => {
  // État local pour la configuration des services
  const [serviceRoutes, setServiceRoutes] = useState<Record<string, string>>(
    formData.config?.services?.routes || {}
  );
  const [hasServiceChanges, setHasServiceChanges] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(formData.config?.services?.folder || '');
  const [currentFiles, setCurrentFiles] = useState<any[]>([]);

  const availableFolders = getAvailableFolders();

  // Mettre à jour les fichiers quand le dossier change
  useEffect(() => {
    if (currentFolder) {
      const files = getServiceFiles(currentFolder);
      setCurrentFiles(files);
      console.log(`📁 Dossier sélectionné: ${currentFolder}`);
      console.log(`📄 Fichiers trouvés:`, files);
    } else {
      setCurrentFiles([]);
    }
  }, [currentFolder]);

  // Synchroniser avec formData au chargement initial uniquement
  useEffect(() => {
    const folder = formData.config?.services?.folder || '';
    const routes = formData.config?.services?.routes || {};
    
    // Mettre à jour seulement si différent
    if (folder !== currentFolder) {
      setCurrentFolder(folder);
    }
    
    // Mettre à jour les routes depuis formData
    if (JSON.stringify(routes) !== JSON.stringify(serviceRoutes)) {
      setServiceRoutes(routes);
    }
  }, [formData.config?.services]);

  // Fonction pour mettre à jour une route
  const handleRouteChange = (fileKey: string, newRoute: string) => {
    const newRoutes = { ...serviceRoutes, [fileKey]: newRoute };
    setServiceRoutes(newRoutes);
    setHasServiceChanges(true);
  };

  // Fonction pour sauvegarder la configuration des services
  const saveServicesConfig = () => {
    const config = {
      folder: currentFolder,
      routes: serviceRoutes
    };
    console.log('💾 Sauvegarde de la config services:', config);
    updateServicesConfig(config);
    setHasServiceChanges(false);
  };

  // Fonction pour changer de dossier
  const handleFolderChange = (newFolder: string) => {
    console.log(`🔄 Changement de dossier: ${newFolder}`);
    setCurrentFolder(newFolder);
    updateServicesConfig({
      folder: newFolder,
      routes: {} // Reset routes
    });
    setServiceRoutes({});
    setHasServiceChanges(false);
  };
  return (
    <section>
      <div className="mb-6">
        <p className="text-gray-400">Système à deux niveaux : sélectionnez d'abord la page, puis choisissez son template. Vous pouvez configurer plusieurs pages avant de sauvegarder.</p>
      </div>

      {/* Sélecteur de page - Niveau 1 */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-white mb-4">1. Choisissez la page à configurer</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <select
              value={selectedPage}
              onChange={(e) => setSelectedPage(e.target.value)}
              className="w-full px-3 py-2 bg-black/20 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              style={{ colorScheme: 'dark' }}
            >
              {[
                { key: 'home', label: '🏠 Accueil' },
                { key: 'contact', label: '📞 Contact' },
                { key: 'about', label: 'ℹ️ À propos' },
                { key: 'boutique', label: '🛍️ Boutique' },
                { key: 'blog', label: '📝 Blog' },
                { key: 'portfolio', label: '🎨 Portfolio' },
                { key: 'services', label: '⚙️ Services' },
                { key: 'faq', label: '❓ FAQ' }
              ].map(page => (
                <option key={page.key} value={page.key} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                  {page.label}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-black/20 p-4 rounded-lg border border-white/10">
            <h4 className="text-sm font-medium text-white mb-2">Pages configurées</h4>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'home', label: 'Accueil' },
                { key: 'contact', label: 'Contact' },
                { key: 'about', label: 'À propos' },
                { key: 'boutique', label: 'Boutique' },
                { key: 'blog', label: 'Blog' },
                { key: 'portfolio', label: 'Portfolio' },
                { key: 'services', label: 'Services' },
                { key: 'faq', label: 'FAQ' }
              ].filter(page =>
                formData.config?.templates?.[page.key] || pendingTemplateChanges[page.key]
              ).map(page => (
                <span
                  key={page.key}
                  className="px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded border border-green-500/30"
                >
                  ✓ {page.label}
                </span>
              ))}
              {![
                'home', 'contact', 'about', 'boutique', 'blog', 'portfolio', 'services', 'faq'
              ].some(key =>
                formData.config?.templates?.[key] || pendingTemplateChanges[key]
              ) && (
                <span className="text-xs text-gray-500">Aucune page configurée</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sélecteur de template - Niveau 2 */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-white mb-4">
          2. Choisissez le template pour "{selectedPage}"
        </h3>
        <div className="bg-white/5 p-6 rounded-lg border border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-3">
                Template pour la page {selectedPage}
              </label>
              <select
                value={pendingTemplateChanges[selectedPage] || formData.config?.templates?.[selectedPage] || ''}
                onChange={(e) => handlePendingTemplateChange(selectedPage, e.target.value)}
                className="w-full px-3 py-2 bg-black/20 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                style={{ colorScheme: 'dark' }}
              >
                <option value="" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Template par défaut</option>
                {(() => {
                  // Filtrer les templates selon la page sélectionnée
                  const pageTemplates = templatePaths.filter(p => {
                    const pathLower = p.toLowerCase();
                    return pathLower.includes(`/${selectedPage}/`) || pathLower.includes(`${selectedPage}.`) || pathLower.includes(`${selectedPage}-`);
                  });
                  return pageTemplates.map(path => (
                    <option key={path} value={path} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                      {path.replace('/src/templates/', '')}
                    </option>
                  ));
                })()}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Dossier scanné: <code>/src/templates/{selectedPage}/*</code>
              </p>
            </div>

            <div className="flex flex-col justify-end">
              <div className="bg-black/20 p-4 rounded-lg border border-white/10">
                <h4 className="text-sm font-medium text-white mb-2">Aperçu de la configuration</h4>
                <div className="text-xs text-gray-400 space-y-1">
                  <div>Page: <span className="text-blue-300">{selectedPage}</span></div>
                  <div>Template actuel: <span className="text-green-300">
                    {formData.config?.templates?.[selectedPage] ?
                      formData.config.templates[selectedPage].replace('/src/templates/', '') :
                      'Par défaut'
                    }
                  </span></div>
                  {pendingTemplateChanges[selectedPage] && (
                    <div>Nouveau template: <span className="text-yellow-300">
                      {pendingTemplateChanges[selectedPage].replace('/src/templates/', '')}
                    </span></div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Résumé des changements en attente */}
      {Object.keys(pendingTemplateChanges).length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-white mb-4">Changements en attente</h3>
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <div className="space-y-2">
              {Object.entries(pendingTemplateChanges).map(([page, template]) => (
                <div key={page} className="flex items-center justify-between text-sm">
                  <span className="text-yellow-300">
                    {page}: {template ? template.replace('/src/templates/', '') : 'Par défaut'}
                  </span>
                  <button
                    onClick={() => {
                      const newChanges = { ...pendingTemplateChanges };
                      delete newChanges[page];
                      setPendingTemplateChanges(newChanges);
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={applyPendingTemplateChanges}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-sm"
              >
                Appliquer les changements
              </button>
              <button
                onClick={() => setPendingTemplateChanges({})}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors text-sm"
              >
                Annuler tout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Header */}
      <div className="mt-10">
        <h3 className="text-lg font-medium text-white mb-4 flex items-center">Header personnalisé</h3>
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <label className="block text-sm font-medium text-white mb-2">Header par défaut</label>
            <select
              value={formData.config?.templates?.headers?.default || ''}
              onChange={(e) => setHeaderTemplateSelection('default', e.target.value)}
              className="w-full px-3 py-2 bg-black/20 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              style={{ colorScheme: 'dark' }}
            >
              <option value="" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>PublicHeader par défaut</option>
              {headerOptions.map(opt => (
                <option key={opt.value} value={opt.value} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">Dossier scanné: <code>/src/templates/headers/*</code></p>
          </div>
        </div>
      </div>

      {/* Templates Header Admin */}
      <div className="mt-10">
        <h3 className="text-lg font-medium text-white mb-4 flex items-center">Header Admin</h3>
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <label className="block text-sm font-medium text-white mb-2">Header Admin (global)</label>
            <select
              value={formData.config?.templates?.admin_headers?.default || ''}
              onChange={(e) => setAdminHeaderTemplateSelection('default', e.target.value)}
              className="w-full px-3 py-2 bg-black/20 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              style={{ colorScheme: 'dark' }}
            >
              <option value="" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Header Admin par défaut (intégré)</option>
              {adminHeaderOptions.map(opt => (
                <option key={opt.value} value={opt.value} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">Dossier scanné: <code>/src/templates/admin-headers/*</code></p>
          </div>
        </div>
      </div>

      {/* Templates Footer */}
      <div className="mt-10">
        <h3 className="text-lg font-medium text-white mb-4 flex items-center">Footer personnalisé</h3>
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <label className="block text-sm font-medium text-white mb-2">Footer par défaut</label>
            <select
              value={formData.config?.templates?.footers?.default || ''}
              onChange={(e) => setFooterTemplateSelection('default', e.target.value)}
              className="w-full px-3 py-2 bg-black/20 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              style={{ colorScheme: 'dark' }}
            >
              <option value="" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>PublicFooter par défaut</option>
              {footerOptions.map(opt => (
                <option key={opt.value} value={opt.value} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">Dossier scanné: <code>/src/templates/footers/*</code></p>
          </div>
        </div>
      </div>

      {/* Configuration des Services */}
      <div className="mt-10">
        <h3 className="text-lg font-medium text-white mb-4 flex items-center">
          <LayoutGrid className="mr-2" size={20} />
          Configuration des Services
        </h3>
        <div className="bg-white/5 p-6 rounded-lg border border-white/10 space-y-6">
          {/* Sélection du dossier de services */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Dossier des services
            </label>
            <select
              value={currentFolder}
              onChange={(e) => handleFolderChange(e.target.value)}
              className="w-full px-3 py-2 bg-black/20 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              style={{ colorScheme: 'dark' }}
            >
              <option value="" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                Aucun (désactiver les services)
              </option>
              {availableFolders.map(folder => (
                <option key={folder.name} value={folder.name} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                  {folder.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Dossier: <code>/src/pages/services/{currentFolder || '[dossier]'}/</code>
            </p>
            {currentFolder && (
              <p className="text-xs text-gray-400 mt-1">
                {availableFolders.find(f => f.name === currentFolder)?.description}
              </p>
            )}
          </div>

          {/* Mapping des routes si un dossier est sélectionné */}
          {currentFolder && currentFiles.length > 0 && (
            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-white">
                  Mapping des routes ({currentFiles.length} services)
                </h4>
                {hasServiceChanges && (
                  <button
                    onClick={saveServicesConfig}
                    className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-sm"
                  >
                    <Save size={16} />
                    Sauvegarder
                  </button>
                )}
              </div>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {currentFiles.map(file => (
                  <ServiceRouteMapper
                    key={file.key}
                    fileName={file.fileName}
                    label={file.label}
                    defaultRoute={file.defaultRoute}
                    currentRoute={serviceRoutes[file.key] || file.defaultRoute}
                    onChange={(route) => handleRouteChange(file.key, route)}
                    isUnique={isRouteUnique(serviceRoutes[file.key] || file.defaultRoute, file.key, serviceRoutes)}
                  />
                ))}
              </div>

              <div className="text-xs text-gray-400 mt-4 p-3 bg-black/20 rounded-lg">
                <p className="flex items-center gap-2">
                  <span className="text-blue-400">💡</span>
                  Les routes seront automatiquement accessibles sous <code className="text-blue-300">/services/[route]</code>
                </p>
                <p className="mt-1 ml-6">
                  Exemple: <code className="text-green-300">/services/{currentFiles[0]?.defaultRoute}</code> → {currentFiles[0]?.fileName}
                </p>
              </div>
            </div>
          )}

          {currentFolder && currentFiles.length === 0 && (
            <div className="border-t border-white/10 pt-6">
              <div className="text-sm text-yellow-400 bg-yellow-500/10 p-4 rounded-lg border border-yellow-500/20">
                <p className="font-medium mb-2">⚠️ Aucun fichier trouvé</p>
                <p className="text-xs text-gray-400">
                  Créez d'abord le dossier <code>/src/pages/services/{currentFolder}/</code>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Puis ajoutez vos fichiers de services (.tsx)
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 text-sm text-gray-400">
        <p>Ajoutez simplement vos fichiers TSX/JSX/TS/JS dans <code>/src/templates/&lt;page&gt;/</code> (ex: <code>/src/templates/home/home-modern.tsx</code>) pour les voir apparaître ici.</p>
      </div>
    </section>
  );
};

// Composant helper pour mapper une route
const ServiceRouteMapper: React.FC<{
  fileName: string;
  label?: string;
  defaultRoute: string;
  currentRoute: string;
  onChange: (route: string) => void;
  isUnique?: boolean;
}> = ({ fileName, label, defaultRoute, currentRoute, onChange, isUnique = true }) => {
  return (
    <div className={`flex items-center gap-3 bg-black/20 p-3 rounded-lg border ${!isUnique ? 'border-red-500/50' : 'border-transparent'}`}>
      <div className="flex-1">
        <div className="text-sm text-gray-300">{label || fileName}</div>
        {label && <div className="text-xs text-gray-500 mt-0.5">{fileName}</div>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">/services/</span>
        <input
          type="text"
          value={currentRoute}
          onChange={(e) => onChange(e.target.value)}
          placeholder={defaultRoute}
          className={`px-2 py-1 bg-black/30 border rounded text-sm text-white focus:outline-none focus:ring-1 ${
            !isUnique ? 'border-red-500 focus:ring-red-500' : 'border-white/10 focus:ring-blue-500'
          }`}
          style={{ width: '180px' }}
        />
      </div>
      {!isUnique && (
        <span className="text-xs text-red-400">⚠️ Doublon</span>
      )}
    </div>
  );
};
